// ═══════════════════════════════════════════════════════════════════════════
// Pedir un precio especial
//
// Lo que el vendedor ve donde antes había un campo que podía mover. La
// diferencia importa: no se le esconde el precio —lo necesita para vender— se
// le quita la mano.
//
// TRES COSAS QUE ESTA PANTALLA HACE A PROPÓSITO:
//
//   · Enseña el precio de HOY junto al que se pide. Una solicitud que dice
//     "430" y nada más obliga a quien autoriza a ir a buscar contra qué. El
//     precio actual se congela al pedir, en la base, no aquí.
//   · Pide el motivo. No es burocracia: "compite con la bodega de al lado" y
//     "el cliente se quejó" se autorizan distinto, y tres semanas después es
//     lo único que explica por qué ese cliente paga menos.
//   · Muestra lo que ya pidió, con su estado. Sin eso vuelve a pedir lo mismo
//     —la base lo rechaza, pero el vendedor no sabría por qué.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { solicitarPrecio, solicitudes, TIPO_SOLICITUD, ESTADO_SOLICITUD }
  from "../datos/precios-solicitud.js";
import { PRICING_RULES } from "../dominio/precios.js";
import { fmt } from "../pdf/comunes.js";
import { Label } from "./basicos.jsx";
import { btnPrimary, btnSecondary, inputStyle, selectStyle } from "./estilos.js";
import { ordenarCatalogo } from "../dominio/remisiones.js";

export function SolicitarPrecio({ cliente, products, membresia, onCerrar }) {
  const tenantId = membresia?.tenant_id;

  const [tipo, setTipo] = useState("precio_producto");
  const [productId, setProductId] = useState("");
  const [valor, setValor] = useState("");
  const [motivo, setMotivo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");
  const [listo, setListo] = useState(false);
  const [mias, setMias] = useState([]);

  const vendibles = ordenarCatalogo((products || []).filter(p => p.category !== "cortesia" && p.grossPrice > 0));

  const cargar = useCallback(async () => {
    if (!tenantId) return;
    try {
      const todas = await solicitudes(tenantId);
      setMias(todas.filter(s => s.client_id === cliente?.id));
    } catch (e) { setError(e.message); }
  }, [tenantId, cliente?.id]);

  useEffect(() => { cargar(); }, [cargar]);

  // Lo que paga hoy. Es lo mismo que congelará la base al guardar; se muestra
  // aquí para que el vendedor escriba el número sabiendo de dónde parte.
  const precioHoy = (() => {
    if (tipo === "descuento_cliente") return null;
    const p = vendibles.find(x => x.id === productId);
    if (!p) return null;
    const esp = cliente?.customPrices?.[productId];
    if (esp !== undefined && esp !== "") return Number(esp);
    const regla = PRICING_RULES[cliente?.pricingRule];
    if (regla?.priceMap && regla.priceMap[productId] !== undefined) return regla.priceMap[productId];
    return p.grossPrice;
  })();

  const num = parseFloat(valor);
  const puedeEnviar = !ocupado && !isNaN(num) && num >= 0 &&
    (tipo === "descuento_cliente" ? num <= 100 : !!productId);

  const enviar = async () => {
    setOcupado(true); setError("");
    try {
      await solicitarPrecio(tenantId, cliente.id, tipo, productId || null, num, motivo);
      setListo(true); setValor(""); setMotivo(""); setProductId("");
      await cargar();
      setTimeout(() => setListo(false), 3000);
    } catch (e) { setError(e.message); }
    setOcupado(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000,
                  display: "flex", alignItems: "flex-start", justifyContent: "center",
                  overflow: "auto", padding: 20 }}
      onClick={onCerrar}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#111", maxWidth: 560, width: "100%", borderRadius: 12,
                 padding: 28, border: "1px solid #222", marginTop: 20 }}>

        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22,
                      color: "#c9a96e", marginBottom: 4 }}>
          Solicitar precio especial
        </div>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 20 }}>
          Para <b style={{ color: "#e8e0d4" }}>{cliente?.name}</b>. Lo autoriza el dueño o
          un gerente, y al autorizarlo se aplica solo.
        </div>

        <div style={{ marginBottom: 14 }}>
          <Label>Qué quieres cambiar</Label>
          <select value={tipo} onChange={e => { setTipo(e.target.value); setValor(""); }}
            style={selectStyle}>
            {Object.values(TIPO_SOLICITUD).map(t => (
              <option key={t.clave} value={t.clave}>{t.etiqueta}</option>
            ))}
          </select>
        </div>

        {tipo === "precio_producto" && (
          <div style={{ marginBottom: 14 }}>
            <Label>Producto</Label>
            <select value={productId} onChange={e => setProductId(e.target.value)} style={selectStyle}>
              <option value="">Elige un producto…</option>
              {vendibles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}

        <div style={{ display: "flex", gap: 14, marginBottom: 14, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 130 }}>
            <Label>Paga hoy</Label>
            <div style={{ padding: "9px 12px", background: "#0d0d0d", border: "1px solid #1a1a1a",
                          borderRadius: 6, fontSize: 14, color: "#888" }}>
              {tipo === "descuento_cliente"
                ? `${cliente?.discount ?? 0}%`
                : precioHoy != null ? fmt(precioHoy) : "—"}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 130 }}>
            <Label>{tipo === "descuento_cliente" ? "Descuento que pides (%)" : "Precio que pides"}</Label>
            <input type="number" min="0" step={tipo === "descuento_cliente" ? "1" : "0.01"}
              max={tipo === "descuento_cliente" ? "100" : undefined}
              value={valor} onChange={e => setValor(e.target.value)} autoFocus
              style={{ ...inputStyle, color: "#a8d060", fontWeight: 600 }} />
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <Label>Por qué</Label>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2}
            placeholder="Ej. Compite con la bodega de al lado; pide 20 cajas al mes"
            style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
          <div style={{ fontSize: 10, color: "#555", marginTop: 4 }}>
            Sin esto, dentro de tres semanas nadie se acuerda de por qué este cliente paga distinto.
          </div>
        </div>

        {error && (
          <div style={{ background: "#1f0d0d", border: "1px solid #3a1a1a", borderRadius: 6,
                        padding: "10px 12px", marginBottom: 14, fontSize: 12, color: "#e8b0b0" }}>
            ⚠️ {error}
          </div>
        )}
        {listo && (
          <div style={{ background: "#0d1f0d", border: "1px solid #1a3a1a", borderRadius: 6,
                        padding: "10px 12px", marginBottom: 14, fontSize: 12, color: "#a8d060" }}>
            ✓ Solicitud enviada. Te enteras aquí mismo cuando la respondan.
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "flex-end" }}>
          <button onClick={onCerrar} style={btnSecondary}>Cerrar</button>
          <button onClick={enviar} disabled={!puedeEnviar}
            style={{ ...btnPrimary, opacity: puedeEnviar ? 1 : 0.4 }}>
            {ocupado ? "Enviando…" : "Enviar solicitud"}
          </button>
        </div>

        {mias.length > 0 && (
          <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid #1e1e1e" }}>
            <div style={{ fontSize: 10, color: "#666", letterSpacing: 2, marginBottom: 8 }}>
              LO QUE HAS PEDIDO PARA ESTE CLIENTE
            </div>
            {mias.map(s => {
              const e = ESTADO_SOLICITUD[s.estado] || ESTADO_SOLICITUD.pendiente;
              const pct = s.tipo === "descuento_cliente";
              return (
                <div key={s.id} style={{ display: "flex", gap: 10, alignItems: "baseline",
                                         padding: "6px 0", borderBottom: "1px solid #141414",
                                         fontSize: 12, flexWrap: "wrap" }}>
                  <span style={{ color: e.color, fontSize: 10, minWidth: 74 }}>{e.etiqueta}</span>
                  <span style={{ color: "#888", flex: 1, minWidth: 120 }}>
                    {s.producto || "Descuento del cliente"}
                  </span>
                  <span style={{ color: "#555" }}>
                    {pct ? `${Number(s.valor_actual)}%` : fmt(s.valor_actual)}
                  </span>
                  <span style={{ color: "#555" }}>→</span>
                  <span style={{ color: e.color }}>
                    {pct ? `${Number(s.valor_pedido)}%` : fmt(s.valor_pedido)}
                  </span>
                  {s.nota_decision && (
                    <span style={{ width: "100%", fontSize: 11, color: "#666" }}>
                      {s.decidio ? `${s.decidio}: ` : ""}{s.nota_decision}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
